import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PaymentModeEditor from "@/components/PaymentModeEditor";

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 p-4">
    <div className="label-tiny mb-3">{title}</div>
    {children}
  </div>
);

export default function PatientProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEditPayment = ["admin", "super_admin", "billing", "accounts"].includes(user?.role);
  const [data, setData] = useState(null);
  const [walkins, setWalkins] = useState([]);
  const [editingPay, setEditingPay] = useState(null);
  const load = () => {
    api.get(`/patients/${id}/summary`).then((r) => setData(r.data));
    api.get(`/walkin-investigations/by-patient/${id}`).then((r) => setWalkins(r.data || [])).catch(() => setWalkins([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!data) return <div>Loading...</div>;
  const p = data.patient;

  return (
    <div className="space-y-4">
      {/* Patient Banner */}
      <div className="bg-blue-50 border border-blue-200 p-4">
        <div className="flex items-start gap-6">
          <div className="w-14 h-14 bg-blue-700 text-white flex items-center justify-center rounded-sm font-display text-lg">
            {p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-xl font-semibold">{p.name}</h1>
              {p.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm">PMJAY</Badge>}
              <Badge variant="outline" className="rounded-sm">{p.blood_group || "N/A"}</Badge>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-4 text-xs">
              <div><span className="label-tiny">UHID</span><div className="font-mono mt-1">{p.uhid}</div></div>
              <div><span className="label-tiny">Age / Gender</span><div className="mt-1">{p.age} / {p.gender}</div></div>
              <div><span className="label-tiny">Mobile</span><div className="font-mono mt-1">{p.mobile}</div></div>
              <div><span className="label-tiny">Relative</span><div className="mt-1">{p.relative_name} · <span className="font-mono">{p.relative_mobile}</span></div></div>
              <div><span className="label-tiny">Address</span><div className="mt-1">{p.address}</div></div>
              <div><span className="label-tiny">Total Billed</span><div className="mt-1 font-mono">{fmtINR(data.totals.billed)}</div></div>
              <div><span className="label-tiny">Balance</span><div className="mt-1 font-mono text-red-700">{fmtINR(data.totals.balance)}</div></div>
              <div><span className="label-tiny">Advance</span><div className="mt-1 font-mono">{fmtINR(data.totals.advance)}</div></div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 rounded-sm h-auto p-0">
          {["overview", "opd", "ipd", "emergency", "walkin", "investigations", "billing"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="rounded-none border-r border-slate-200 px-4 py-2 text-xs uppercase tracking-wider">{t === "walkin" ? "Walk-in" : t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid grid-cols-2 gap-4">
          <Section title="Financial Summary">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-slate-500">Total Billed</div><div className="font-mono text-right">{fmtINR(data.totals.billed)}</div>
              <div className="text-slate-500">Total Paid</div><div className="font-mono text-right">{fmtINR(data.totals.paid)}</div>
              <div className="text-slate-500">Advance</div><div className="font-mono text-right">{fmtINR(data.totals.advance)}</div>
              <div className="text-slate-500">Refund</div><div className="font-mono text-right">{fmtINR(data.totals.refund)}</div>
              <div className="font-semibold text-slate-900 border-t pt-2 mt-2">Balance</div><div className="font-mono font-semibold text-right border-t pt-2 mt-2 text-red-700">{fmtINR(data.totals.balance)}</div>
            </div>
          </Section>
          <Section title="Recent Activity">
            <ul className="text-sm space-y-2">
              {data.opd.slice(0, 3).map((o) => <li key={o.id} className="flex justify-between"><span>OPD · {o.doctor_name}</span><span className="text-slate-500">{fmtDT(o.created_at)}</span></li>)}
              {data.ipd.slice(0, 3).map((o) => <li key={o.id} className="flex justify-between"><span>IPD · {o.doctor_name}</span><span className="text-slate-500">{fmtDT(o.admission_date)}</span></li>)}
            </ul>
          </Section>
        </TabsContent>

        <TabsContent value="opd" className="mt-4">
          <Section title="OPD Visits">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">OPD No</th><th>Doctor</th><th>Diagnosis</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {data.opd.map((o) => (
                  <tr key={o.id} className="border-b">
                    <td className="font-mono py-2">{o.opd_no}</td>
                    <td>{o.doctor_name}</td>
                    <td className="text-xs">{o.diagnosis}</td>
                    <td>{fmtDT(o.created_at)}</td>
                    <td><Link to={`/opd/${o.id}`} className="text-blue-700">Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </TabsContent>
        <TabsContent value="ipd" className="mt-4">
          <Section title="IPD Admissions">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">IPD No</th><th>Doctor</th><th>Bed</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {data.ipd.map((o) => (
                  <tr key={o.id} className="border-b">
                    <td className="font-mono py-2">{o.ipd_no}</td>
                    <td>{o.doctor_name}</td>
                    <td>{o.bed_no || "-"}</td>
                    <td><Badge className="rounded-sm">{o.status}</Badge></td>
                    <td><Link to={`/ipd/${o.id}`} className="text-blue-700">Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </TabsContent>
        <TabsContent value="emergency" className="mt-4">
          <Section title="Emergency Visits">
            <div className="text-sm">{data.emergency.length} records</div>
          </Section>
        </TabsContent>
        <TabsContent value="walkin" className="mt-4">
          <Section title="Walk-in Investigations · Payments">
            {walkins.length === 0 ? (
              <div className="text-sm text-slate-500">No walk-in investigation visits recorded.</div>
            ) : (
              <table className="w-full text-sm" data-testid="patient-walkin-table">
                <thead><tr className="text-left border-b text-slate-500">
                  <th className="py-2">Date</th><th>OPD No</th><th>Tests</th>
                  <th>Payment</th><th>Reference</th>
                  <th className="text-right">Amount</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {walkins.map((w) => (
                    <tr key={w.id} className="border-b" data-testid={`patient-walkin-row-${w.id}`}>
                      <td className="py-2 text-xs">{(w.visit_date || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="font-mono text-xs">{w.opd_no}</td>
                      <td className="text-xs">
                        <div className="flex flex-wrap gap-1">
                          {(w.investigations || []).slice(0, 3).map((n, i) => (
                            <Badge key={`${w.id}-t-${i}`} variant="outline" className="text-[10px]">{n}</Badge>
                          ))}
                          {(w.investigations || []).length > 3 && <span className="text-[10px] text-slate-400">+{w.investigations.length - 3}</span>}
                        </div>
                      </td>
                      <td>
                        <Badge variant="outline" className={`text-[10px] ${
                          w.payment_status === "PAID" ? "border-teal-400 text-teal-700 bg-teal-50" :
                          w.payment_status === "SCHEME" ? "border-purple-400 text-purple-700 bg-purple-50" :
                          w.payment_status === "PARTIAL" ? "border-amber-400 text-amber-700 bg-amber-50" :
                          "border-red-400 text-red-700 bg-red-50"
                        }`}>{w.payment_mode || "CASH"}</Badge>
                        {w.payment_scheme_name && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{w.payment_scheme_name}</div>
                        )}
                      </td>
                      <td className="font-mono text-xs">{w.payment_reference_no || "—"}</td>
                      <td className="text-right font-mono">{fmtINR(w.total_amount)}</td>
                      <td>
                        {w.imported_to_ipd_id ? (
                          <Badge className="bg-blue-100 text-blue-800 rounded-sm text-[10px]">In IPD Bill</Badge>
                        ) : (
                          <span className="text-[10px] text-slate-500">{w.payment_status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </TabsContent>
        <TabsContent value="investigations" className="mt-4">
          <Section title="Investigations">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b text-slate-500"><th className="py-2">Name</th><th>Category</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {data.investigations.map((i) => (
                  <tr key={i.id} className="border-b"><td>{i.name}</td><td>{i.category}</td><td>{i.status}</td><td>{fmtDT(i.created_at)}</td></tr>
                ))}
              </tbody>
            </table>
          </Section>
        </TabsContent>
        <TabsContent value="billing" className="mt-4 space-y-4">
          <Section title="Bill Items">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b text-slate-500"><th className="py-2">Category</th><th>Description</th><th>Qty</th><th>Rate</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {data.bill_items.map((b) => (
                  <tr key={b.id} className="border-b"><td>{b.category}</td><td>{b.description}</td><td>{b.quantity}</td><td>{fmtINR(b.rate)}</td><td className="text-right font-mono">{fmtINR(b.amount)}</td></tr>
                ))}
                {data.bill_items.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-xs text-slate-400">No bill items yet.</td></tr>}
              </tbody>
            </table>
          </Section>

          <Section title={`Payments / Receipts (${(data.payments || []).length})`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b text-slate-500">
                  <th className="py-2">Receipt</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Mode</th>
                  <th>Ref No.</th>
                  <th className="text-right">Amount</th>
                  {canEditPayment && <th className="w-20"></th>}
                </tr>
              </thead>
              <tbody>
                {(data.payments || []).map((p) => (
                  <tr key={p.id} className={`border-b ${p.is_void ? "opacity-40 line-through" : ""}`} data-testid={`patient-pay-${p.receipt_no || p.id}`}>
                    <td className="py-1.5 font-mono text-xs">{p.receipt_no || "—"}</td>
                    <td className="text-xs">{fmtDT(p.created_at)}</td>
                    <td className="text-xs capitalize">{p.type}</td>
                    <td>
                      <Badge className={`rounded-sm text-[10px] ${
                        p.mode === "PMJAY" ? "bg-teal-100 text-teal-800"
                        : p.mode === "PACKAGE" ? "bg-amber-100 text-amber-800"
                        : p.mode === "UPI" || p.mode === "GPay" ? "bg-indigo-100 text-indigo-800"
                        : "bg-emerald-100 text-emerald-800"
                      }`}>{(p.mode || "CASH").toUpperCase()}</Badge>
                    </td>
                    <td className="font-mono text-xs text-slate-600">{p.reference_no || "—"}</td>
                    <td className={`text-right font-mono ${p.type === "refund" ? "text-rose-700" : "text-slate-800"}`}>
                      {p.type === "refund" ? "-" : ""}{fmtINR(p.amount)}
                    </td>
                    {canEditPayment && (
                      <td className="text-right">
                        {!p.is_void && p.type === "payment" && (
                          <button
                            data-testid={`patient-edit-mode-${p.id}`}
                            onClick={() => setEditingPay(p)}
                            className="p-1 text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm"
                            title="Change payment mode (audit-logged)">
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {(data.payments || []).length === 0 && (
                  <tr><td colSpan={canEditPayment ? 7 : 6} className="py-4 text-center text-xs text-slate-400">No payments received yet.</td></tr>
                )}
              </tbody>
            </table>
          </Section>
        </TabsContent>
      </Tabs>

      {editingPay && (
        <PaymentModeEditor
          payment={{ ...editingPay, patient_name: p.name }}
          onClose={() => setEditingPay(null)}
          onSaved={() => { setEditingPay(null); load(); }}
        />
      )}
    </div>
  );
}
